/**
 * API client for inventory endpoints
 */

import { api, ensureApiAuthenticated } from '../services/api';
import { TcgplayerListingSnapshot } from '../lib/tcgplayerListing';

export interface InventoryItem {
  id: string;
  variant_id: string;
  quantity: number;
  cost_basis: number | null;
  location: string | null;
  updated_at: string;
  total_value: number | null;
  // Variant details for rich display
  product_name?: string | null;
  set_name?: string | null;
  set_code?: string | null;
  condition?: string | null;
  foil?: boolean | null;
  etched?: boolean | null;
  language?: string | null;
  collector_number?: string | null;
  product_line?: string | null;
  /** Reference catalog for this row: mtgjson or tcgcsv. */
  catalog_source?: 'mtgjson' | 'tcgcsv' | null;
  product_category?: string | null;
  /** Real product_variants.id. ``variant_id`` on this payload is the product id. */
  product_variant_id?: string | null;
  printing?: string | null;
  tcgplayer_listing?: TcgplayerListingSnapshot | null;
  game_metadata?: Array<{ key: string; label: string; value: string }> | null;
  // MTGJSON enrichment fields
  rarity?: string | null;
  oracle_text?: string | null;
  type_line?: string | null;
  mana_cost?: string | null;
  cmc?: number | null;
  power?: string | null;
  toughness?: string | null;
  color_identity?: string[] | null;
  colors?: string[] | null;
  keywords?: string[] | null;
  scryfall_id?: string | null;
  reverse_scryfall_id?: string | null;
  image_url?: string | null;
  reverse_image_url?: string | null;
  is_dual_faced?: boolean | null;
  edhrec_rank?: number | null;
  edhrec_saltiness?: number | null;
  is_game_changer?: boolean | null;
  is_reserved?: boolean | null;
  leadership_skills?: Record<string, boolean> | null;
  legalities?: Record<string, string> | null;
  tcgplayer_market?: number | null;
  manapool_market?: number | null;
  ck_buylist?: number | null;
  preferred_price?: number | null;
  calculated_preferred_price?: number | null;
  preferred_price_override?: number | null;
  manapool_listing_price?: number | null;
  /** Condition×finish Manapool marketplace low from market intel */
  manapool_low?: number | null;
  /** SyncBase policy sell stamp (four-signal max, then age blend) */
  policy_price?: number | null;
  policy_price_debug?: {
    tcg_market?: number | null;
    tcg_low?: number | null;
    manapool_market?: number | null;
    manapool_low?: number | null;
    high?: number | null;
    low?: number | null;
    blend?: string | null;
    age_days?: number | null;
    policy_price?: number | null;
  } | null;
  /** Accumulated Manapool marketplace comps (last ~365 days) */
  manapool_recent_sales?: Array<{
    sold_at?: string | null;
    price_cents: number;
    quantity?: number;
  }> | null;
  tcgplayer_id?: string | null;
}

export interface InventoryCopy {
  id: string;
  acquisition_cost: number | null;
  acquired_at: string | null;
  status: string;
  location_bin: string | null;
  location_notes: string | null;
  condition: string;
  is_foil: boolean;
  language: string | null;
  printing: string | null;
}

export interface UpdateInventoryCopyRequest {
  acquisition_cost?: number;
  location?: string;
  condition?: string;
  language?: string;
  printing?: string;
}

export interface UpdatePreferredPriceOverrideRequest {
  condition: string;
  is_foil: boolean;
  language?: string;
  printing?: string;
  preferred_price_override?: number | null;
}

export interface PreferredPriceOverrideResponse {
  product_id: string;
  variant_id: string;
  preferred_price: number | null;
  calculated_preferred_price: number | null;
  preferred_price_override: number | null;
  updated_at: string | null;
}

export interface InventoryListParams {
  limit?: number;
  offset?: number;
  product_name?: string;  // Search by product name
  status?: string;
  set_name?: string;      // Filter by set name
  card_number?: string;   // Filter by collector number
  condition?: string;
  location?: string;
  min_quantity?: number;
  max_quantity?: number;
  min_cost?: number;
  max_cost?: number;
  acquired_after?: string;
  acquired_before?: string;
  /** Legacy foil boolean. Omitted when ``printing`` is set. */
  foil?: boolean;
  /** Printing stamp: normal, foil, etched, signed, reverse_holofoil. */
  printing?: string;
  /** Preferred game filter: mtg, pokemon, or lorcana. */
  game?: string;
  /** Canonical products.product_line. Normalized to the same game as `game`. */
  product_line?: string;
  language?: string;
  has_cost_basis?: boolean;
  sort_by?: 'name' | 'acquired_at' | 'acquisition_cost' | 'quantity' | 'condition' | 'status';
  sort_dir?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  total_copies?: number;
  /** False when the tcgplayer channel will not fan listings out. */
  tcgplayer_outbound_enabled?: boolean;
  filter_options?: {
    sets: {
      total: number;
      options: Array<{
        name: string;
        copies: number;
      }>;
    };
    locations: {
      options: string[];
    };
  };
  limit: number;
  offset: number;
}

/**
 * List inventory items with pagination and filters
 */
export async function listInventory(
  params: InventoryListParams = {}
): Promise<PaginatedResponse<InventoryItem>> {
  await ensureApiAuthenticated();

  const response = await api.get<PaginatedResponse<InventoryItem>>('/inventory', {
    params,
  });

  return response.data;
}

export interface InventoryStats {
  min_quantity: number;
  max_quantity: number;
  min_cost: number;
  max_cost: number;
}

export interface InventoryStatsParams {
  /** Preferred game filter: mtg, pokemon, or lorcana. */
  game?: string;
  /** Canonical products.product_line. Normalized to the same game as `game`. */
  product_line?: string;
}

/**
 * Min/max quantity and cost for the inventory filters.
 * Pass `game` so the ranges match a per-game Lookup.
 */
export async function getInventoryStats(
  params: InventoryStatsParams = {},
): Promise<InventoryStats> {
  await ensureApiAuthenticated();
  const response = await api.get<InventoryStats>('/inventory/stats', { params });
  return response.data;
}

export async function listInventoryCopies(params: {
  product_id: string;
  condition?: string;
  is_foil?: boolean;
  language?: string | null;
  printing?: string | null;
}): Promise<InventoryCopy[]> {
  await ensureApiAuthenticated();
  const response = await api.get<InventoryCopy[]>('/inventory/copies', { params });
  return response.data;
}

export async function updateInventoryCopy(copyId: string, data: UpdateInventoryCopyRequest): Promise<InventoryCopy> {
  await ensureApiAuthenticated();
  const response = await api.patch<InventoryCopy>(`/inventory/copies/${copyId}`, data);
  return response.data;
}

export async function updatePreferredPriceOverride(
  productId: string,
  data: UpdatePreferredPriceOverrideRequest,
): Promise<PreferredPriceOverrideResponse> {
  await ensureApiAuthenticated();
  const response = await api.patch<PreferredPriceOverrideResponse>(`/inventory/products/${productId}/variant-pricing`, data);
  return response.data;
}

export interface RemoveCopyRequest {
  reason: 'UNFOUND' | 'DAMAGED' | 'LOST' | 'PROMOTIONAL' | 'DONATION' | 'SAMPLE' | 'ERROR_CORRECTION' | 'OTHER';
  track_as_shrink: boolean;
  notes?: string;
}

export interface RemoveCopyResponse {
  message: string;
  shrink_record?: {
    id: string;
    product_id: string;
    quantity: number;
    reason: string;
    created_at: string;
  };
}

/**
 * Remove a copy from inventory with optional shrink tracking
 */
export async function removeCopy(copyId: string, data: RemoveCopyRequest): Promise<RemoveCopyResponse> {
  await ensureApiAuthenticated();
  const response = await api.delete<RemoveCopyResponse>(`/inventory/copies/${copyId}`, { data });
  return response.data;
}
